import { HelpArticle } from "@/components/HelpArticle/HelpArticle";
import { Screenshot } from "@/components/Screenshot/Screenshot";
import { createFileRoute } from "@tanstack/react-router";
import leggTilHjemmelserklæring from "./legg-til-hjemmelserklaering.png";
import kjøpesumSkifteoppgjør from "./kjopesum-skifteoppgjor.png";
import leggTilSkjøte from "./legg-til-skjote.png";
import vedleggskategori from "./vedleggskategori.png";

const title = "Dødsbo – hjemmelserklæring og skjøte med skifteattest";

export const Route = createFileRoute("/_authenticated-user/hjelp/dodsbo-hjemmelserklaering-og-skifteattest/")({
  head: () => ({
    meta: [{ title: `${title} | Alice & Bob` }],
  }),
  component: Page,
});

function Page() {
  return (
    <HelpArticle title={title}>
      <h2>Hjemmelserklæring</h2>
      <p>Her skal eiendommen overskjøtes fra avdød til arvingene.</p>

      <h3>Opprett hjemmelserklæring</h3>
      <p>Trykk på «Legg til dokument» og velg «Hjemmelserklæring».</p>
      <Screenshot
        src={leggTilHjemmelserklæring}
        width={345}
        alt="Menyen «Legg til dokument» åpen, med «Hjemmelserklæring» markert."
      />

      <p>
        Kjøpesum og salgsverdi/avgiftsgrunnlag er automatisk satt til <strong>kr 0</strong>, og omsetningstype og
        dokumentavgiftsgrunnlag er automatisk satt til <strong>skifteoppgjør</strong>.
      </p>
      <Screenshot
        src={kjøpesumSkifteoppgjør}
        width={900}
        alt="Seksjonene «Kjøpesum» og «Salgsverdi/avgiftsgrunnlag» med beløp kr 0, omsetningstype «Skifteoppgjør (6)» og dokumentavgiftsårsak «Skifteoppgjør (S)»."
      />

      <p>
        <strong>NB!</strong> Sørg for at kun avdød står under «erklæring om sivilstand».
      </p>

      <h3>Hvem kan signere hjemmelserklæringen?</h3>
      <ul>
        <li>
          De arvinger som har påtatt seg gjeldsansvaret på vegne av de selvskiftende arvinger iht. arveloven § 116. Hvem
          disse personene er, vil fremkomme av skifteattesten.
        </li>
        <li>
          Dersom eiendommen skal overføres til en testamentsarving i henhold til testament, kan testamentsarvingen
          skrive under på hjemmelserklæringen alene, under den forutsetningen at han/hun har påtatt seg gjeldsansvaret
          iht. arveloven § 116.
        </li>
        <li>Alle arvinger som fremkommer av skifteattesten.</li>
        <li>
          Fullmektig for arvingene. De gjeldsovertagende arvingene kan gi fullmakt til en av arvingene eller til en
          utenforstående. Fullmakter i original, eller kopi med inneståelseserklæring, må legges ved.
        </li>
        <li>
          Testamentsfullbyrder. En testamentsfullbyrder er en som arvelater i testament oppnevner til å disponere over
          boet. Denne ansees som fullmektig i boet ved privat skifte. Dersom det er oppnevnt en testamentsfullbyrder vil
          det fremkomme av skifteattesten.
        </li>
        <li>Eiendomsmegler, megler, eiendomsmeglerfullmektig, advokat eller advokatfullmektig.</li>
      </ul>

      <h2>Skjøte</h2>
      <p>Her skal eiendommen overskjøtes videre til nye eiere/ny fordeling blant arvingene.</p>

      <h3>Opprett skjøte</h3>
      <p>Trykk på «Legg til dokument» og velg «Skjøte».</p>
      <p>Her skal eiendommen overskjøtes fra arvinger til ny eier.</p>
      <Screenshot src={leggTilSkjøte} width={287} alt="Menyen «Legg til dokument» åpen, med «Skjøte» markert." />

      <p>
        <strong>NB!</strong> Sørg for at arving(ene) står under «erklæring om sivilstand».
      </p>

      <h3>Hvem skal signere skjøtet?</h3>
      <p>
        Her skal alle som står oppført under «fra» i skjøtet signere, med mindre det foreligger fullmakter som tillater
        noe annet. Fullmakter lastes opp som vedlegg i kategorien «Salgsfullmakt».
      </p>

      <h2>Vedlegg</h2>
      <p>Legg til skifteattesten (og eventuelle andre vedlegg) som vedlegg til både hjemmelserklæringen og skjøtet.</p>
      <Screenshot
        src={vedleggskategori}
        width={268}
        alt="Dialogen «Last opp vedlegg» med vedleggskategorien «Skifteattest, uskifteattest og bostyrerattest ved dødsboskifte» markert."
      />

      <h2>Send til Kartverket</h2>
      <p>
        Ved innsendelse til tinglysing er det viktig at hjemmelserklæringen ligger som nr. 1 og skjøtet som nr. 2 for å
        få riktig rekkefølge i innsendelsen til Kartverket. Dersom det skal tinglyses nytt pant legges pantedokument fra
        bank som nr. 3.
      </p>
      <p>
        Når dokumentene sendes inn til Kartverket går det til manuell behandling og man kan forvente bekreftelse etter
        noen timer.
      </p>
    </HelpArticle>
  );
}
